import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormulaireRegleDeduction } from './FormulaireRegleDeduction';
import { TableRegles } from './TableRegles';
import { TableOccurrences } from './TableOccurrences';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')}` } });

// 🧾 Phase 2 : onglet « Déductions » — sélecteur installateur, formulaire, règles, occurrences.
export function DeductionsInstallateurs({ installateurs }) {
  const [installateurId, setInstallateurId] = useState('');
  const [regles, setRegles] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const noms = Object.fromEntries(installateurs.map((i) => [i.id, i.nom]));

  const charger = useCallback(async () => {
    const q = installateurId ? `?installateur_id=${installateurId}` : '';
    const [r, o] = await Promise.all([
      axios.get(`${API}/api/installateurs/deductions/regles${q}`, auth()),
      axios.get(`${API}/api/installateurs/deductions/occurrences${q}`, auth()),
    ]);
    setRegles(r.data.regles || []);
    setOccurrences(o.data.occurrences || []);
  }, [installateurId]);

  useEffect(() => { charger().catch(() => toast.error('Impossible de charger les déductions.')); }, [charger]);

  const action = async (fn, succes) => {
    setEnCours(true);
    try { await fn(); toast.success(succes); await charger(); return true; }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Erreur'); return false; }
    finally { setEnCours(false); }
  };

  return (
    <div className="space-y-4" data-testid="onglet-deductions-contenu">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium">Installateur</label>
        <select className="border rounded-md h-9 px-2 text-sm bg-white" value={installateurId} data-testid="selecteur-installateur" onChange={(e) => setInstallateurId(e.target.value)}>
          <option value="">Tous les installateurs</option>
          {installateurs.map((i) => <option key={i.id} value={i.id}>{i.nom}</option>)}
        </select>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Nouvelle déduction</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <FormulaireRegleDeduction key={installateurId} installateurs={installateurs} installateurId={installateurId} enCours={enCours}
            onSoumettre={(corps) => action(() => axios.post(`${API}/api/installateurs/deductions/regles`, corps, auth()), 'Déduction créée')} />
          <p className="text-xs text-slate-500 border-l-2 border-amber-300 pl-2" data-testid="aide-frequences">
            <b>Hebdomadaire</b> : chaque semaine (dimanche → samedi) à partir de la semaine de début. <b>Aux 28 jours</b> : toutes les 4 semaines à partir
            de la semaine de début. <b>Mensuel</b> : la semaine qui contient le 1<sup>er</sup> du mois. <b>Unique</b> : une seule fois, à la semaine cible.
            Une déduction s'applique en entier seulement si le montant payable de la semaine le permet; sinon elle passe « En attente » et sera
            reportée sur la prochaine semaine avec revenus (la semaine d'origine est conservée). Le montant payable n'est jamais négatif.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Règles ({regles.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <TableRegles regles={regles} nomsInstallateurs={noms}
            onStatut={(r, statut) => action(() => axios.put(`${API}/api/installateurs/deductions/regles/${r.id}`, { statut_regle: statut }, auth()), statut === 'active' ? 'Règle réactivée' : 'Règle désactivée')}
            onAnnuler={(r) => { const raison = window.prompt('Motif d\'annulation de la règle :', 'règle annulée'); if (raison) action(() => axios.delete(`${API}/api/installateurs/deductions/regles/${r.id}`, { ...auth(), data: { raison } }), 'Règle annulée'); }} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Occurrences ({occurrences.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <TableOccurrences occurrences={occurrences} regles={regles}
            onAnnuler={(o) => { const raison = window.prompt('Motif d\'annulation (obligatoire) :'); if (raison) action(() => axios.post(`${API}/api/installateurs/deductions/occurrences/${o.id}/annuler`, { raison }, auth()), 'Occurrence annulée'); }} />
        </CardContent>
      </Card>
    </div>
  );
}
